import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, Loader, Minus, Sparkles, Trophy, X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { predictionsAPI } from '../services/predictionsApi';
import { resolveParticipant } from '../utils/resolveParticipant';
import { PlayerAvatar } from './PredictionResultCard';

function firstName(fullName) {
  return (fullName || '').trim().split(/\s+/)[0] || fullName;
}

// One person's avatar + name, used for both the primary player and (in
// doubles) their partner — each gets their own avatar rather than the
// partner appearing as bare text next to the primary player's photo.
function PersonLine({ player, reverse, primary, isWinner, theme }) {
  return (
    <div className={`flex items-center gap-1.5 min-w-0 ${reverse ? 'flex-row-reverse' : ''}`}>
      <PlayerAvatar name={player.name} id={player.id} size="sm" />
      {/* min-w-0 here too — flex items default to min-width:auto (their
          content's natural width), so without this a long name would push
          past the row instead of the sibling Trophy icon squeezing it into
          an ellipsis. */}
      <div className={`flex items-center gap-1 min-w-0 ${reverse ? 'flex-row-reverse' : ''}`}>
        <span className={`min-w-0 truncate ${primary ? 'text-sm font-semibold' : 'text-xs font-medium'} ${isWinner ? theme.text : `${theme.textMuted}${primary ? '' : ' opacity-80'}`}`}>
          {firstName(player.name)}
        </span>
        {/* Both doubles partners share the win — trophy isn't gated on primary */}
        {isWinner && <Trophy className="w-3 h-3 text-amber-400 flex-shrink-0" />}
      </div>
    </div>
  );
}

// One side of a matchup: primary player's avatar + name, with the doubles
// partner (if any) as a second avatar + name stacked below. Both get the
// same winner styling/trophy since a doubles win belongs to the whole team,
// not just the primary player. min-w-0 + truncate let this shrink fluidly
// instead of forcing the row wider than the viewport.
function Matchup({ player, partner, align, isWinner, theme }) {
  const reverse = align === 'right';
  return (
    <div className={`flex flex-col gap-0.5 min-w-0 ${reverse ? 'items-end text-right' : 'items-start text-left'}`}>
      <PersonLine player={player} reverse={reverse} primary isWinner={isWinner} theme={theme} />
      {partner && <PersonLine player={partner} reverse={reverse} isWinner={isWinner} theme={theme} />}
    </div>
  );
}

// Compact prediction indicator. No history yet → a single dash (explained
// once by the legend up top) instead of repeating "Not enough history yet"
// on every row — that repeated text was the single biggest space hog and
// is what forced the horizontal scrollbar on narrow screens.
function PredictionCell({ result, error, theme }) {
  if (error) {
    return <Minus className={`w-4 h-4 ${theme.textMuted} opacity-40 mx-auto`} title={error} />;
  }

  return (
    <div className="flex items-center justify-center gap-1">
      <span className="text-xs font-bold text-blue-500 dark:text-cyan-300 tabular-nums">{result.team1WinPct}%</span>
      <div className={`w-9 h-1.5 rounded-full overflow-hidden flex flex-shrink-0 ${theme.backgroundSecondary}`}>
        <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-400" style={{ width: `${result.team1WinPct}%` }} />
        <div className="h-full bg-gradient-to-r from-orange-400 to-rose-500" style={{ width: `${result.team2WinPct}%` }} />
      </div>
      <span className="text-xs font-bold text-rose-500 dark:text-rose-300 tabular-nums">{result.team2WinPct}%</span>
    </div>
  );
}

function PredictAllResultRow({ matchNumber, player1, player1Partner, player2, player2Partner, result, error, theme }) {
  const hasPrediction = !!result && !error && result.team1WinPct != null;
  const p1Wins = hasPrediction && result.team1WinPct >= result.team2WinPct;

  return (
    <div className={`grid grid-cols-[1.25rem_1fr_auto_1fr] items-center gap-2 px-2 sm:px-3 py-2.5 border-b ${theme.border} last:border-0 ${!hasPrediction ? 'opacity-70' : ''}`}>
      <span className={`text-xs ${theme.textMuted} text-center`}>{matchNumber}</span>
      <Matchup player={player1} partner={player1Partner} align="left" isWinner={hasPrediction && p1Wins} theme={theme} />
      <PredictionCell result={result} error={error} theme={theme} />
      <Matchup player={player2} partner={player2Partner} align="right" isWinner={hasPrediction && !p1Wins} theme={theme} />
    </div>
  );
}

/**
 * "Predict All" modal — resolves every eligible match in the schedule
 * (fetching each unique team's roster at most once) and predicts all of
 * them with a single POST /predictions/matches call, then lists results
 * grouped by round. One fluid grid layout for every screen size — no
 * separate mobile/desktop markup, no horizontal scrollbar.
 */
const PredictAllModal = ({ games, sportTypeId, sportName, onClose }) => {
  const { theme } = useTheme();
  const [status, setStatus] = useState('resolving'); // 'resolving' | 'predicting' | 'done' | 'error'
  const [rows, setRows] = useState([]); // [{ matchId, matchNumber, roundName, player1, player1Partner, player2, player2Partner }]
  const [resultsByMatchId, setResultsByMatchId] = useState({});
  const [errorMsg, setErrorMsg] = useState(null);
  const teamCache = useRef(new Map());

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const eligible = games.filter(g => g.participant_a_id && g.participant_b_id);
      if (eligible.length === 0) {
        setErrorMsg('No matches with both players assigned yet.');
        setStatus('error');
        return;
      }

      const resolved = await Promise.all(eligible.map(async (game) => {
        const base = { matchId: game.id, matchNumber: game.match_number, roundName: game.round_name };
        try {
          const [a, b] = await Promise.all([
            resolveParticipant(game.participant_a_type, game.participant_a_id, game.participant_a_name, teamCache.current),
            resolveParticipant(game.participant_b_type, game.participant_b_id, game.participant_b_name, teamCache.current),
          ]);
          return {
            ...base,
            player1: a.primary, player1Partner: a.partner,
            player2: b.primary, player2Partner: b.partner,
          };
        } catch (err) {
          return {
            ...base,
            player1: { id: game.participant_a_id, name: game.participant_a_name },
            player2: { id: game.participant_b_id, name: game.participant_b_name },
            resolveError: err.message || 'Could not load roster.',
          };
        }
      }));
      if (cancelled) return;

      setRows(resolved);
      setStatus('predicting');

      const payload = resolved
        .filter(r => !r.resolveError)
        .map(r => ({
          matchId: r.matchId,
          sportId: sportTypeId,
          player1Id: r.player1.id,
          player2Id: r.player2.id,
          ...(r.player1Partner && { player3Id: r.player1Partner.id }),
          ...(r.player2Partner && { player4Id: r.player2Partner.id }),
        }));

      try {
        const results = payload.length > 0 ? await predictionsAPI.predictMatches(payload) : [];
        if (cancelled) return;
        setResultsByMatchId(Object.fromEntries(results.map(r => [r.matchId, r])));
        setStatus('done');
      } catch (err) {
        if (cancelled) return;
        setErrorMsg(err?.message || 'Prediction failed. Please try again.');
        setStatus('error');
      }
    })();

    return () => { cancelled = true; };
  }, [games, sportTypeId]);

  // Group rows by round, preserving schedule order, so "Round" doesn't
  // repeat on every line — matches how the schedule table itself reads.
  const roundGroups = useMemo(() => {
    const groups = [];
    const byName = new Map();
    for (const row of rows) {
      const key = row.roundName || 'Round';
      if (!byName.has(key)) {
        const group = { name: key.replace(/^Matchday\s+/i, 'Round '), rows: [] };
        byName.set(key, group);
        groups.push(group);
      }
      byName.get(key).rows.push(row);
    }
    return groups;
  }, [rows]);

  const readyCount = rows.filter(r => {
    const res = resultsByMatchId[r.matchId];
    return !r.resolveError && res && !res.error && res.team1WinPct != null;
  }).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className={`relative w-full max-w-2xl rounded-2xl border ${theme.border} ${theme.card} shadow-2xl overflow-hidden max-h-[80vh] flex flex-col`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b ${theme.border} flex-shrink-0`}>
          <div className="flex items-center gap-2">
            <Sparkles size={15} className="text-amber-500" />
            <span className={`text-sm font-semibold ${theme.text}`}>
              Prediction{sportName ? ` — ${sportName}` : ''}
            </span>
          </div>
          <button onClick={onClose} className={`p-1.5 rounded-lg ${theme.secondaryHover} ${theme.textMuted}`}>
            <X size={14} />
          </button>
        </div>

        {/* Summary + legend */}
        {status === 'done' && rows.length > 0 && (
          <div className={`px-5 py-2 text-xs ${theme.textMuted} border-b ${theme.border} flex-shrink-0 flex items-center justify-between flex-wrap gap-x-4 gap-y-1`}>
            <span><span className="text-blue-500 dark:text-cyan-300 font-semibold">{readyCount}</span> of {rows.length} confident</span>
            {readyCount < rows.length && (
              <span className="flex items-center gap-1 whitespace-nowrap">
                <Minus className="w-3.5 h-3.5 opacity-40" /> not enough history yet
              </span>
            )}
          </div>
        )}

        {/* Body */}
        {status === 'error' ? (
          <p className={`text-sm ${theme.textMuted} text-center py-10 px-6`}>{errorMsg}</p>
        ) : status !== 'done' ? (
          <div className="flex flex-col items-center gap-3 py-12">
            <Loader size={22} className="text-blue-500 animate-spin" />
            <p className={`text-xs ${theme.textMuted}`}>
              {status === 'resolving' ? 'Loading team rosters…' : 'Analysing all matches…'}
            </p>
          </div>
        ) : (
          <div className="overflow-y-auto">
            {roundGroups.map(group => (
              <div key={group.name}>
                <div className={`${theme.backgroundSecondary} px-2 sm:px-3 py-1.5 text-xs font-medium ${theme.textMuted} flex items-center gap-1 border-b ${theme.border}`}>
                  <ChevronRight className="w-3 h-3 opacity-50" />
                  {group.name}
                </div>
                {group.rows.map(row => (
                  <PredictAllResultRow
                    key={row.matchId}
                    matchNumber={row.matchNumber}
                    player1={row.player1}
                    player1Partner={row.player1Partner}
                    player2={row.player2}
                    player2Partner={row.player2Partner}
                    result={resultsByMatchId[row.matchId]}
                    error={row.resolveError || resultsByMatchId[row.matchId]?.error}
                    theme={theme}
                  />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PredictAllModal;
